import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Skeleton.module.css'

type SkeletonVariant = 'rect' | 'circle' | 'text'

type SkeletonProps = {
  variant?: SkeletonVariant
  width?: string | number
  height?: string | number
} & HTMLAttributes<HTMLDivElement>

export function Skeleton({
  variant = 'rect',
  width,
  height,
  className,
  style,
  ...props
}: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        styles.skeleton,
        {
          [styles['skeleton--circle']]: variant === 'circle',
          [styles['skeleton--text']]: variant === 'text',
        },
        className,
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      {...props}
    />
  )
}

type SkeletonGroupProps = {
  count?: number
  gap?: string
  className?: string
  'aria-label'?: string
  children: ReactNode
}

export function SkeletonGroup({
  count = 1,
  gap = '1rem',
  className,
  children,
  'aria-label': ariaLabel,
}: SkeletonGroupProps) {
  return (
    <div
      className={cn(styles.group, className)}
      style={{ gap }}
      aria-busy="true"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index}>{children}</div>
      ))}
    </div>
  )
}
